import * as React from "react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { Pencil, Plus } from "lucide-react"

import { BOOK_META } from "@/components/BookMark"
import { DataState } from "@/components/DataState"
import { WorkPanel } from "@/components/WorkPanel"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { useToast } from "@/components/ui/use-toast"
import { useRbac } from "@/lib/auth"
import { formatAmount, formatDate, formatPercent } from "@/lib/format"
import {
  MANAGEMENT_ACTION_LABELS,
  STATUTORY_ACTION_LABELS,
  TAX_ACTION_LABELS,
  type TreatmentRule,
} from "@/lib/types"
import { cn } from "@/lib/utils"
import { onWriteError } from "@/lib/writeErrors"

import { TreatmentRuleEditor } from "@/routes/masters/TreatmentRuleEditor"
import { fetchTreatmentRules, saveTreatmentRule } from "@/routes/masters/api"
import { t } from "@/lib/i18n"

const STATUS_VARIANT: Record<string, "success-soft" | "warning-soft" | "outline"> = {
  vigente: "success-soft",
  programada: "warning-soft",
}

export function TreatmentRulesPage() {
  const { can } = useRbac()
  const { toast } = useToast()
  const queryClient = useQueryClient()
  const [editing, setEditing] = React.useState<TreatmentRule | null>(null)
  const [creating, setCreating] = React.useState(false)

  const query = useQuery({ queryKey: ["treatment-rules"], queryFn: fetchTreatmentRules })

  const mutation = useMutation({
    mutationFn: (payload: { rule: Partial<TreatmentRule>; reason: string }) =>
      saveTreatmentRule({ data: payload.rule, reason: payload.reason }),
    onSuccess: () => {
      toast({
        variant: "success",
        title: "Regla guardada",
        description: "Version nueva con vigencia. El P&L gerencial se recalcula desde su fecha.",
      })
      setEditing(null)
      setCreating(false)
      void queryClient.invalidateQueries({ queryKey: ["treatment-rules"] })
    },
    onError: onWriteError("la regla"),
  })

  const rules = query.data ?? []

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <p className="max-w-[80ch] text-xs leading-[1.15rem] text-muted-foreground">
          {t("Quien decide que entra al P&L, que solo a contabilidad y que solo a lo tributario es el usuario, desde la app y sin deploy. Cada cambio es una regla nueva con vigencia, no una edicion destructiva.")}</p>
        <Button size="sm" disabled={!can("edit")} onClick={() => setCreating(true)}>
          <Plus />
          {t("Nueva regla")}</Button>
      </div>

      {/* Leyenda de los tres libros: fija el codigo de color de la tabla. */}
      <div className="flex flex-wrap gap-x-6 gap-y-2 rounded-md border bg-surface-sunken px-3 py-2">
        {(["statutory", "tax", "management"] as const).map((kind) => {
          const meta = BOOK_META[kind]
          const Icon = meta.icon
          return (
            <div key={kind} className="flex items-start gap-2">
              <span
                aria-hidden="true"
                className="mt-0.5 h-3.5 w-[2px] rounded-full"
                style={{ background: meta.token }}
              />
              <div>
                <div className="flex items-center gap-1 text-2xs font-semibold" style={{ color: meta.token }}>
                  <Icon className="h-3 w-3" />
                  {meta.label}
                </div>
                <p className="text-2xs text-muted-foreground">{meta.blurb}</p>
              </div>
            </div>
          )
        })}
      </div>

      <WorkPanel
        title={t("Reglas de tratamiento")}
        meta={
          <Badge variant="outline" mono>
            {rules.length}
          </Badge>
        }
        footnote={t("Editar una regla no la sobrescribe: crea una version con nueva vigencia. Los periodos cerrados conservan la regla que estaba vigente cuando se cerraron.")}
      >
        <DataState
          loading={query.isLoading}
          error={query.error}
          onRetry={() => void query.refetch()}
          empty={rules.length === 0}
          emptyMessage={t("Todavia no hay reglas de tratamiento.")}
          emptyHint={t("Sin reglas, todo hecho economico entra igual en los tres libros. La primera regla suele ser la de multas y sanciones: gasto contable, no deducible para el fisco.")}
          emptyAction={
            <Button size="sm" disabled={!can("edit")} onClick={() => setCreating(true)}>
              <Plus />
              {t("Crear la primera regla")}</Button>
          }
          columns={[6, 16, 14, 16, 16, 16, 10]}
        >
          <Table containerClassName="max-h-[calc(100vh-24rem)]">
            <TableHeader>
              <TableRow>
                <TableHead className="w-20">{t("Regla")}</TableHead>
                <TableHead className="w-52">{t("Nombre")}</TableHead>
                <TableHead className="w-44">{t("Alcance")}</TableHead>
                <TableHead className="w-52 border-l-2 !border-l-book-statutory/50 text-book-statutory">
                  {t("Societario")}</TableHead>
                <TableHead className="w-52 border-l-2 !border-l-book-tax/50 text-book-tax">
                  {t("Tax")}</TableHead>
                <TableHead className="w-52 border-l-2 !border-l-book-management/50 text-book-management">
                  {t("Management")}</TableHead>
                <TableHead className="w-40 border-l">{t("Vigencia")}</TableHead>
                <TableHead className="w-48">{t("Autor / motivo")}</TableHead>
                <TableHead className="w-20" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {rules.map((rule) => (
                <TableRow key={rule.id} className="[&>td]:h-auto [&>td]:py-2 [&>td]:align-top">
                  <TableCell className="code text-muted-foreground">{rule.id}</TableCell>
                  <TableCell className="font-medium">{rule.name}</TableCell>
                  <TableCell className="text-2xs">
                    <span className="uppercase tracking-[0.06em] text-muted-foreground">
                      {rule.scopeType}
                    </span>
                    <div className="code mt-px text-foreground">{rule.scopeValue}</div>
                  </TableCell>

                  <BookCell className="border-l-2 !border-l-book-statutory/50">
                    <Badge variant="statutory">
                      {STATUTORY_ACTION_LABELS[rule.statutory.action]}
                    </Badge>
                    {rule.statutory.limitPct ? (
                      <Note mono>limite {formatPercent(rule.statutory.limitPct, 2)}</Note>
                    ) : null}
                    {rule.statutory.note ? <Note>{rule.statutory.note}</Note> : null}
                  </BookCell>

                  <BookCell className="border-l-2 !border-l-book-tax/50">
                    <Badge variant="tax">{TAX_ACTION_LABELS[rule.tax.action]}</Badge>
                    {rule.tax.limitPct ? (
                      <Note mono>limite {formatPercent(rule.tax.limitPct, 2)}</Note>
                    ) : null}
                    {rule.tax.legalBasis ? <Note>{rule.tax.legalBasis}</Note> : null}
                    {rule.tax.note ? <Note>{rule.tax.note}</Note> : null}
                  </BookCell>

                  <BookCell className="border-l-2 !border-l-book-management/50">
                    <Badge variant="management">
                      {MANAGEMENT_ACTION_LABELS[rule.management.action]}
                    </Badge>
                    {rule.management.targetAccount ? (
                      <Note mono>→ {rule.management.targetAccount}</Note>
                    ) : null}
                    {rule.management.normalizedValue ? (
                      <Note mono>valor {formatAmount(rule.management.normalizedValue)}</Note>
                    ) : null}
                    {rule.management.excludeFromEbitda ? (
                      <div className="mt-1">
                        <Badge variant="outline">{t("fuera del EBITDA")}</Badge>
                      </div>
                    ) : null}
                    {rule.management.note ? <Note>{rule.management.note}</Note> : null}
                  </BookCell>

                  <TableCell className="border-l text-2xs">
                    <div className="code">
                      {formatDate(rule.effectiveFrom)} →{" "}
                      {rule.effectiveTo ? formatDate(rule.effectiveTo) : "sin termino"}
                    </div>
                    <div className="mt-1">
                      <Badge variant={STATUS_VARIANT[rule.status] ?? "outline"}>{rule.status}</Badge>
                    </div>
                  </TableCell>

                  <TableCell className="text-2xs">
                    <div className="font-medium text-foreground">{rule.author}</div>
                    <p className="clamp-2 leading-4 text-muted-foreground" title={rule.reason}>
                      {rule.reason}
                    </p>
                  </TableCell>

                  <TableCell className="text-right">
                    <Button
                      size="xs"
                      variant="ghost"
                      disabled={!can("edit")}
                      className="opacity-70 transition-opacity group-hover/row:opacity-100 group-focus-within/row:opacity-100"
                      onClick={() => setEditing(rule)}
                    >
                      <Pencil />
                      {t("Editar")}</Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </DataState>
      </WorkPanel>

      <TreatmentRuleEditor
        open={Boolean(editing) || creating}
        onOpenChange={(open) => {
          if (!open) {
            setEditing(null)
            setCreating(false)
          }
        }}
        rule={editing}
        pending={mutation.isPending}
        onSubmit={(rule, reason) => mutation.mutateAsync({ rule, reason }).then(() => undefined)}
      />
    </div>
  )
}

function BookCell({ className, children }: { className?: string; children: React.ReactNode }) {
  return <TableCell className={cn("space-y-0.5", className)}>{children}</TableCell>
}

function Note({ children, mono }: { children: React.ReactNode; mono?: boolean }) {
  return (
    <p className={cn("mt-0.5 text-2xs leading-4 text-muted-foreground", mono && "code")}>
      {children}
    </p>
  )
}
